import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { WorkerSchema, WorkerCreateInputSchema, WorkerUpdateInputSchema, type Worker, type WorkerCreateInput, type WorkerUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `worker` (personne qui travaille pour l'org, org-wide).
 *
 * Calque le PATRON asset : l'entrée est VALIDÉE par Zod
 * (`WorkerCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit jamais
 * un document pour découvrir ensuite qu'il est invalide.
 *
 * Périmètre v0 org-wide : seuls les champs non sensibles sont écrits ici. Les
 * données RH (statut contractuel, coût horaire, dates d'embauche) vivront dans
 * un sous-document privé restreint (cf. WIP), pas via ce repository.
 */

export type { WorkerCreateInput };

/** Nom de la collection Firestore de la famille worker. */
export const WORKERS_COLLECTION = 'workers';

/**
 * Repository typé pour la famille `worker`.
 * Toute lecture est validée par `WorkerSchema`, toute écriture par
 * `WorkerCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface WorkerRepository {
  create(input: WorkerCreateInput): Promise<Worker>;
  update(id: string, patch: WorkerUpdateInput): Promise<Worker>;
  list(options?: ListOptions): Promise<Worker[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Worker | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Worker>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Worker>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Worker>;
}

const workerConverter = createConverter<Worker>((raw: DocumentData) => WorkerSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createWorkerRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): WorkerRepository {
  const col = collection(db, WORKERS_COLLECTION).withConverter(workerConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Worker>(db, col, WORKERS_COLLECTION, getActorId);

  return {
    async create(input: WorkerCreateInput): Promise<Worker> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = WorkerCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.skills !== undefined) optional['skills'] = fields.skills;
      if (fields.bookableOnline !== undefined) optional['bookableOnline'] = fields.bookableOnline;
      if (fields.defaultPlaceId !== undefined) optional['defaultPlaceId'] = fields.defaultPlaceId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        name: fields.name,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Worker);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return WorkerSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: WorkerUpdateInput): Promise<Worker> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = WorkerUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, WORKERS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`worker ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Worker[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Worker | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
